import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  SPACE_ELEMENTS_OF_MODAL,
  STACK_ERROR_SX,
} from "@components/common/dialog/faro-dialog";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import {
  MemberAutocomplete,
  SelectedMember,
} from "@components/common/member-autocomplete/member-autocomplete";
import { useGroupMembersContext } from "@context-providers/group-members-provider";
import { useCompanyId } from "@context-providers/company-id-provider";
import { blue, Dialog, neutral, InfoCircleFillIcon } from "@flat-ui";
import { Alert, Stack } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { isObject } from "es-toolkit/compat";
import { useEffect, useRef, useState } from "react";

interface ChangeManagementDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback invoked when the dialog is closed */
  onClose: () => void;
}

/** Dialog to change the project manager and group of the current project */
export function ChangeManagementDialogContent({
  isOpen,
  onClose,
}: ChangeManagementDialogProps): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();

  const { project, groupMembers, setSelectedGroupId, selectedGroupId } =
    useGroupMembersContext();

  const [selectedProjectManager, setSelectedProjectManager] =
    useState<SelectedMember>(null);
  const isSubmittingRef = useRef(false);
  const [isChangingManagement, setIsChangingManagement] =
    useState<boolean>(false);

  // Prefill the project manager with the current project manager, if the current group of the project is selected
  // Empty it for other groups
  useEffect(() => {
    if (project && isOpen && project.group) {
      if (project.group.id !== selectedGroupId) {
        setSelectedProjectManager(null);
        return;
      }

      const projectManager =
        groupMembers.find(
          (member) =>
            member.identity === project.managers?.projectManager?.identity
        ) ?? null;
      setSelectedProjectManager(projectManager);
    }
  }, [isOpen, groupMembers, project, selectedGroupId]);

  useEffect(() => {
    if (isOpen) {
      isSubmittingRef.current = false;
    }
  }, [isOpen]);

  /**
   * Sends a request to the backend to change the project manager and group of the project
   */
  async function changeProjectManagerAndGroup(): Promise<void> {
    // Synchronous guard against very fast double-clicks before state rerender.
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    // Theoretically the user should not be able to reach this state
    // if the project manager was not provided but do this for type safety
    // and as an edge case if our members selection didn't work properly.
    if (!selectedProjectManager) {
      throw new Error("Project manager is required!");
    }

    setIsChangingManagement(true);
    try {
      if (!isObject(selectedProjectManager)) {
        const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"company"> = {
          assignments: [
            {
              identities: [selectedProjectManager],
              role: "PROJECT_MANAGER",
            },
          ],
        };

        await faroCoreApiClient.company.addMembersToCompany({
          companyId,
          payload,
        });
      }

      const payload: FaroCoreApiTypes.UpdateProjectDetailsPayloadProps = {
        projectManagerIdentity: isObject(selectedProjectManager)
          ? selectedProjectManager.identity
          : selectedProjectManager,
        groupId: String(selectedGroupId),
      };

      await dispatch(
        updateProjectDetails({ projectId: project?.id, payload })
      ).unwrap();
      onClose();
    } finally {
      setIsChangingManagement(false);
    }
  }

  return (
    <Dialog
      title="Change Management"
      confirmText="Change"
      open={isOpen}
      onConfirm={changeProjectManagerAndGroup}
      isConfirmDisabled={!selectedProjectManager || isChangingManagement}
      onCancel={isChangingManagement ? undefined : onClose}
      showSpinner={isChangingManagement}
      disabled={isChangingManagement}
    >
      <Alert
        icon={<InfoCircleFillIcon />}
        severity="info"
        sx={{
          marginBottom: SPACE_ELEMENTS_OF_MODAL,
          backgroundColor: blue[100],
          color: neutral[1000],
        }}
      >
        Be aware that changing a group can ultimately impact the project
        manager.
      </Alert>
      <Stack sx={STACK_ERROR_SX}>
        <LabelWithHelp title="Group" />
        <GroupAutoComplete
          callbackOnGroupSelected={(groupId) => setSelectedGroupId(groupId)}
          selectedGroupId={selectedGroupId}
          shouldOnlyShowGroupsWithGroupManager={true}
        />
      </Stack>
      <Stack>
        <LabelWithHelp title="Manager" isDisabled={!selectedGroupId} />
        <MemberAutocomplete
          members={groupMembers}
          selectedMember={selectedProjectManager}
          onOptionChange={setSelectedProjectManager}
          isDisabled={!selectedGroupId}
          placeholder="Choose who can manage this project..."
        />
      </Stack>
    </Dialog>
  );
}
